/**
 * FlowLifecycleManager
 *
 * Thin scaffold around the CoPilot Guide that wires:
 *
 *   • Leave guard       — Wouter route block + browser beforeunload.
 *   • Switch guard      — JourneyInstance switcher confirm-prompt.
 *   • Restart / Discard confirm modals — opened on demand via the
 *                         `controls.startRestart` /
 *                         `controls.startDiscard` callbacks the host
 *                         wires to the guide footer's two action
 *                         buttons. Each modal calls the matching
 *                         atomic backend mutation (which cleans up
 *                         the hero assessment row in lockstep with
 *                         the guide-state row, via the
 *                         LifecycleStrategyRegistry).
 *   • Widget state      — exposed via FlowLifecycleContext for any
 *                         widget rendered in the host's children to
 *                         opt into via `useWidgetState()`.
 *   • Autosave wiring   — `lastSavedAt` + `hasSaveError` for the
 *                         host's `<AutosaveIndicator>`.
 *   • Telemetry         — `flow_lifecycle.*` events for product
 *                         analytics.
 *
 * NOT here anymore (removed 2026-04-27):
 *   • Resume modal — the engine restores chat history directly; the
 *     user lands on the conversation, no decision modal in the way.
 *   • URL signals (`?retake=1` / `?restart=1` / `?discard=1` /
 *     `?resume=skip`) — host pages run mutations directly.
 *   • Retake modal — moved to host-page `<RetakeConfirmModal>`.
 *
 * Hosts use it via render-prop:
 *
 *   <FlowLifecycleManager flowId="checkup" journeyInstanceId={id}
 *                         onAfterDiscard={() => navigate('/checkup')}>
 *     {(controls, state) => (
 *       <CoPilotGuide
 *         ...
 *         onRestart={() => controls.startRestart()}
 *         onCancel={() => controls.startDiscard()}
 *         onProgressChange={(p) => controls.reportProgress(p)}
 *       />
 *     )}
 *   </FlowLifecycleManager>
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  useCoPilotGuideState,
  useRestartGuide,
  useDiscardGuide,
  usePatchGuideWidgetState,
} from '@tonnie/sdk';

import { captureCustomEvent } from '@/core/lib/observability';
import { useJourneyInstance } from '@/core/contexts/JourneyInstanceContext';
import { useCopilotLogger } from '../lib/use-copilot-logger';
import { useCopilotCorrelation } from '../lib/correlation-context';

import { flowLifecycleRegistry } from './registry';
import type { FlowId, FlowLifecycleControls, FlowLifecycleState } from './types';
import { useFlowLeaveGuard } from './use-flow-leave-guard';
import { ConfirmModal } from './lifecycle-modals';
import { FlowLifecycleContext, type FlowLifecycleContextValue } from './flow-lifecycle-context';
import { clearSessionState } from '../components/guide/guide-session';

interface FlowLifecycleManagerProps {
  flowId: FlowId;
  /** Scopes guide-state lookups to a specific journey (most flows). */
  journeyInstanceId?: string;
  /**
   * Set to true (default) for flows that ALWAYS belong to a journey
   * — assessments, setup, etc. When the host is still loading the
   * journey context, the manager renders a small "loading" state
   * INSTEAD of querying the server with no journeyId (which would
   * silently match cross-journey rows pre-2026-04-25). Journey
   * Builder is the only flow that opts out (it CREATES a journey).
   */
  requireJourney?: boolean;
  /**
   * Total number of user-visible tasks in the guide. Hosts pass
   * `guideConfig.content.tasks.filter(t => t.type !== 'Terminal' &&
   * t.type !== 'Escape').length`. The manager uses this for the
   * "Step N of M" label in the resume modal — without it the manager
   * falls back to a noisy heuristic that produces "Step 1 of 1 — 33%"
   * when completedTasks=0 (math wedge: max(currentStep, round(0/p) ×
   * 100) = 1). Optional; manager defaults to the legacy heuristic if
   * omitted, but every host SHOULD pass it.
   */
  totalSteps?: number;
  /**
   * True if the user has at least one previously-completed result for
   * this flow on this journey. Used to gate the "View my last result"
   * button on the resume modal — for a first-time-attempt user there
   * is no last result, so the button is hidden.
   */
  hasPreviousResult?: boolean;
  /**
   * Called AFTER a successful restart (server confirmed). Use to
   * invalidate query caches the host owns (e.g. `useCheckUpAssessment`).
   * The manager already invalidates the guide-state query.
   */
  onAfterRestart?: () => void;
  /**
   * Called AFTER a successful retake (server confirmed). Same usage
   * as onAfterRestart — typical pattern is to navigate the user to
   * the start of the assessment URL or invalidate caches.
   */
  onAfterRetake?: () => void;
  /**
   * Called AFTER a successful discard. Default behaviour: navigate
   * back to the hero homepage (e.g. `/checkup`). Provide this to
   * override (e.g. navigate to dashboard).
   *
   * Can return a Promise — the manager awaits it before closing the
   * modal so async host cleanup (e.g. CheckUp's companion
   * `cancelCheckUpMutation` that soft-deactivates the
   * checkup_assessments row) finishes before the user is moved on.
   */
  onAfterDiscard?: () => void | Promise<void>;
  /**
   * The render-prop. Receives lifecycle controls + state for the
   * host to wire to its CoPilotGuide.
   */
  children: (
    controls: FlowLifecycleControls & {
      reportDirty: (dirty: boolean) => void;
      /**
       * Preferred over `reportDirty` — pipe `<CoPilotGuide
       * onProgressChange>` here. The manager records the live percent
       * (used by the leave modal copy) AND derives `isDirty` from it
       * (`> 0 && < 100`).
       */
      reportProgress: (percent: number) => void;
    },
    state: FlowLifecycleState
  ) => React.ReactNode;
}

export function FlowLifecycleManager({
  flowId,
  journeyInstanceId,
  requireJourney = true,
  totalSteps: totalStepsFromHost,
  // `hasPreviousResult` was used by the removed ResumeModal's "View
  // my last result" button. Hosts now own retake/results UX and pass
  // this through to their own modals if needed. Kept on the prop
  // type for back-compat; no-op here.
  hasPreviousResult: _hasPreviousResult = false,
  onAfterRestart,
  // onAfterRetake removed — retake is now host-page driven; the
  // RetakeConfirmModal navigates the user itself. Prop kept on the type
  // for back-compat; no-op here.
  onAfterRetake: _onAfterRetake,
  onAfterDiscard,
  children,
}: FlowLifecycleManagerProps) {
  const config = flowLifecycleRegistry[flowId];

  // Normalise empty-string journeyId to undefined so we never send
  // `?journeyInstanceId=` (empty) to the server. Pre-fix this caused
  // the repository to drop the journey filter entirely and return
  // cross-journey state. See guide-state.repository.ts findActive
  // for the corresponding server-side guard.
  const scopedJourneyId =
    journeyInstanceId && journeyInstanceId.length > 0 ? journeyInstanceId : undefined;

  // Wait for the host to provide a real journeyId before querying
  // server state. Without this gate, the manager would briefly query
  // with no scope on initial mount / journey switch, race the host's
  // journey context, and either render a stale cross-journey resume
  // modal or get a cache hit it then has to invalidate.
  // `journeyMissing` was used by the removed resume-effect to bail
  // when activeInstance was still loading. Hosts now own that gate.
  const _journeyMissing = requireJourney && !scopedJourneyId;
  void _journeyMissing;

  const { data: guideState, isLoading: stateLoading } = useCoPilotGuideState(
    flowId,
    scopedJourneyId
  );

  const log = useCopilotLogger('CP:Manager', { flowId, journeyId: scopedJourneyId });
  const correlation = useCopilotCorrelation();

  // ── Manager mount/unmount + guideState change tracker ────────────
  useEffect(() => {
    log.debug('mount', { data: { requireJourney } });
    return () => {
      log.debug('unmount');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    log.debug('guide-state.changed', {
      data: {
        stateLoading,
        guideState: guideState
          ? {
              status: guideState.status,
              currentChatId: guideState.currentChatId,
              completedChats: guideState.completedChats?.length ?? 0,
              progress: guideState.progress,
            }
          : null,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideState, stateLoading]);

  // markResume mutation removed — used by the deleted Resume modal's
  // "Continue" path. With chat history restored directly by the
  // engine, there's nothing to "ack" anymore.
  const restartMutation = useRestartGuide();
  // useRetakeGuide removed — retake mutation now fires from
  // RetakeConfirmModal on the host pages.
  const discardMutation = useDiscardGuide();
  const patchWidgetStateMutation = usePatchGuideWidgetState();

  // ── Modal state ────────────────────────────────────────────────────
  const [openModalRaw, setOpenModal] = useState<FlowLifecycleState['openModal']>('none');
  const openModal = openModalRaw;
  // Autosave indicator — successful patch flips `lastSavedAt`; failed
  // patch flips `hasSaveError`. Hosts subscribe via `<AutosaveIndicator>`.
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [hasSaveError, setHasSaveError] = useState(false);
  /**
   * Tracks whether the currently-open modal was opened by a URL signal
   * (`?retake=1`, `?discard=1`, `?restart=1`). When the user cancels
   * such a modal we must navigate them away from the assessment URL —
   * leaving them on `/checkup/assessment?retake=1` with `isResume
   * Acknowledged === false` strands them on a blank page (host returns
   * null from its render-prop, guide never mounts). Cancelling a
   * URL-signal modal therefore behaves as "take me back to where I
   * came from" — the hero homepage.
   */
  // The auto-opening Resume modal and its `isResumeAcknowledged`
  // gate are gone (2026-04-27). The engine restores chat history
  // directly; hosts unconditionally mount their guide. The leave-
  // guard and switch-guard now arm whenever there's `isDirty` work,
  // without the additional ack precondition that used to gate them.
  // setIsResumeAcknowledged removed — no callers since the resume
  // modal is gone. The boolean above stays as a permanent `true` for
  // back-compat with hosts that still read it from the render-prop.
  const [isDirty, setIsDirty] = useState(false);
  // Live progress reported by the host's CoPilotGuide. Used by modal
  // context with priority over `guideState.progress` because the
  // server save is debounced ~2s — at the moment the user clicks
  // "Leave", the live progress (e.g. 17% for TelePortal Welcome step)
  // is fresher than what's persisted (which might still be 0% from
  // a moment ago). Bug observed 2026-04-25 on TelePortal Setup leave
  // modal showing "0%" while the page header showed "17%".
  const [livePercent, setLivePercent] = useState<number | null>(null);
  const [pendingLeaveResolver, setPendingLeaveResolver] = useState<
    ((allowed: boolean) => void) | null
  >(null);

  /**
   * Where to send the user when they manually escape the resume modal
   * (X / Escape / overlay click), cancel a URL-signal-driven
   * destructive modal, or land on the assessment URL with a cancelled
   * guide and no explicit retake/restart/discard signal.
   *
   * Goes to the hero homepage — the homepage's routing decides the
   * actual surface (Welcome / completed / resume) based on real server
   * state. See `FALLBACK_BY_FLOW` for the per-flow target.
   *
   * Declared HERE (above the resume-effect) so the resume-effect can
   * include it in its deps without hitting TDZ.
   */
  // resolveCancelExitPath removed — only used by the deleted resume-
  // effect / ResumeModal cancel path. Nothing in the manager
  // navigates anymore.

  // ── Resume detection on mount — DELETED ──────────────────────────
  // The auto-opening Resume modal (and its URL-signal counterparts
  // `?retake=1` / `?restart=1` / `?discard=1` / `?resume=skip`) are
  // gone. The new architecture:
  //   • Engine restores prior chat history if the server has any,
  //     else fresh-starts (see use-co-pilot-guide.ts).
  //   • Host pages own the "what should I do?" decision surface:
  //     hero homepage routes by assessment-table status, results
  //     view owns its own Retake confirmation modal.
  //   • Inside the assessment URL, the user sees their conversation
  //     directly. Restart and Cancel are bottom-of-guide actions
  //     wired through `controls.startRestart` / `controls.startDiscard`.
  //
  // No state machine to keep in sync, no drift to paper over, no
  // modal stacking, no remount loops.

  // ── Confirm modal handlers (Restart / Discard / Leave) ────────────
  // Cancel for any confirm modal. Just closes the modal — there's no
  // URL-signal cleanup or auto-navigation anymore.
  const closeModal = useCallback(() => {
    setOpenModal('none');
  }, []);

  // In-flight guards — prevent double-firing when a confirm modal's
  // primary button is double-clicked, OR when the closure-staleness
  // bug used to cause a re-prompt that the user then re-confirmed.
  // Refs (not state) so they don't need to be in deps and don't
  // trigger renders. Reset on success/failure of the awaited
  // mutation. See log evaluation 2026-04-27 — "BUG-2: Discard fires
  // twice".
  const restartInFlightRef = useRef(false);
  const discardInFlightRef = useRef(false);

  const handleRestartConfirm = useCallback(async () => {
    if (restartInFlightRef.current) {
      log.debug('restart.skipped.in-flight');
      return;
    }
    restartInFlightRef.current = true;
    // Stamp a fresh correlation id for the whole restart action so
    // backend logs (lifecycle strategy + reconcile worker if any)
    // share a single trace with this FE attempt.
    correlation.beginAction('restart');
    log.info('restart.start');
    try {
      // The /restart endpoint atomically calls
      // LifecycleStrategy.onRestart server-side, which cancels any
      // in-flight hero assessment row for the same talent+journey
      // BEFORE the guide-state row resets. The next StartAssessment
      // on the assessment URL creates a fresh row.
      await restartMutation.mutateAsync({ flowId, journeyId: scopedJourneyId });

      log.info('restart.ok');
      captureCustomEvent('flow_lifecycle.restart_confirmed', { flowId });
      // Clear the localStorage session — without this the engine
      // restores the pre-restart chat from cache and the user sees the
      // same conversation they tried to wipe.
      clearSessionState(flowId, scopedJourneyId ?? null);
      // Clear `isDirty` SYNCHRONOUSLY before running the host's
      // onAfterRestart. We use `flushSync` (not a plain setState)
      // because the host's callback typically calls `navigate(...)`
      // INSIDE this same handler — and `useFlowLeaveGuard`'s patched
      // `pushState` reads `enabled` from a closure that captures
      // `isDirty` at render time. Without `flushSync`, React batches
      // the update and the next render hasn't happened yet by the
      // time `setLocation` runs, so the leave-guard intercepts the
      // navigation with the STALE `enabled: true` and prompts a
      // "Step away?" modal AFTER the user already chose to restart.
      //
      // `flushSync` forces React to apply the state update + run the
      // useFlowLeaveGuard cleanup (which restores the original,
      // un-patched `pushState`) before we proceed. The subsequent
      // `setLocation` then goes through clean.
      //
      // See log evaluation 2026-04-27 — "BUG-1: Discard triggers a
      // phantom leave-guard prompt" applied to all three flows
      // (TelePortal / QultureUp / Care) plus restart on TelePortal.
      flushSync(() => {
        setIsDirty(false);
        setLivePercent(null);
      });
      onAfterRestart?.();
      setOpenModal('none');
    } catch (err) {
      log.error(
        'restart.failed',
        { data: { err: (err as Error).message } },
        {
          error: err instanceof Error ? err : new Error(String(err)),
        }
      );
      captureCustomEvent('flow_lifecycle.restart_failed', {
        flowId,
        error: (err as Error).message,
      });
      setOpenModal('none');
    } finally {
      restartInFlightRef.current = false;
    }
  }, [restartMutation, flowId, scopedJourneyId, onAfterRestart, log, correlation]);

  // handleRetakeConfirm REMOVED — retake moved to host-page modals
  // (RetakeConfirmModal). The manager no longer participates in retake.

  const handleDiscardConfirm = useCallback(async () => {
    if (discardInFlightRef.current) {
      log.debug('discard.skipped.in-flight');
      return;
    }
    discardInFlightRef.current = true;
    correlation.beginAction('discard');
    log.info('discard.start');
    try {
      // The /discard endpoint atomically calls
      // LifecycleStrategy.onCancel server-side, which cancels the
      // hero assessment row BEFORE flipping the guide-state row to
      // `cancelled`. No manual orchestration in the host's
      // onAfterDiscard required.
      await discardMutation.mutateAsync({ flowId, journeyId: scopedJourneyId });

      log.info('discard.ok');
      captureCustomEvent('flow_lifecycle.discard_confirmed', { flowId });
      // Clear localStorage so a stale local cache doesn't rehydrate
      // the discarded chat on the next mount.
      clearSessionState(flowId, scopedJourneyId ?? null);
      // SYNCHRONOUSLY clear `isDirty` before the host's
      // onAfterDiscard runs — see the matching `flushSync` call in
      // `handleRestartConfirm` above for the full rationale. Without
      // this, `useFlowLeaveGuard`'s patched `pushState` intercepts
      // the host's navigate and prompts a "Step away?" modal AFTER
      // the user already chose to discard. Observed in the
      // 2026-04-27 log on TelePortal / QultureUp / Care discards
      // ("BUG-1: Discard triggers a phantom leave-guard prompt").
      flushSync(() => {
        setIsDirty(false);
        setLivePercent(null);
      });
      // Host's `onAfterDiscard` typically just navigates back to
      // the hero homepage and refreshes caches.
      await onAfterDiscard?.();
      setOpenModal('none');
    } catch (err) {
      log.error(
        'discard.failed',
        { data: { err: (err as Error).message } },
        {
          error: err instanceof Error ? err : new Error(String(err)),
        }
      );
      captureCustomEvent('flow_lifecycle.discard_failed', {
        flowId,
        error: (err as Error).message,
      });
      setOpenModal('none');
    } finally {
      discardInFlightRef.current = false;
    }
  }, [discardMutation, flowId, scopedJourneyId, onAfterDiscard, log, correlation]);

  // ── Leave guard ────────────────────────────────────────────────────
  // The Wouter intercept calls this; we open the leave modal and
  // resolve the promise based on Stay / Leave choice.
  const onAttemptedLeave = useCallback(
    (intendedUrl: string) => {
      log.info('leave.prompt', {
        data: { intendedUrl, isDirty },
      });
      return new Promise<boolean>((resolve) => {
        setPendingLeaveResolver(() => resolve);
        setOpenModal('leave');
        captureCustomEvent('flow_lifecycle.leave_modal_shown', { flowId });
      });
    },
    [flowId, scopedJourneyId, isDirty, log]
  );

  // Ref-indirect access to the (later-declared) flush so this
  // callback can fire it without a circular use-before-decl.
  // `flushPendingWidgetPatches` is bound below where the manager owns
  // the widget set; we forward through the ref so the leave-guard
  // path stays at the top of the file (it's the user-visible
  // contract). Radar 2026-04-28 — M-1.
  const flushFnsHandleRef = useRef<(() => Promise<void>) | null>(null);

  const resolveLeave = useCallback(
    async (allowed: boolean) => {
      log.info('leave.resolve', { data: { allowed } });
      // Drain any debounced widget patches BEFORE the navigation
      // proceeds so server state matches what the user just saw on
      // their screen. Without this, a user clicking "Leave" mid-typing
      // arrives at the next page with the server still missing their
      // last 500ms of input — and a peer tab opens the same flow with
      // stale widget state. Promise.allSettled inside
      // `flushPendingWidgetPatches` already isolates per-widget
      // failures; we additionally swallow any aggregate exception so a
      // misbehaving widget never traps the user on the current URL.
      if (allowed && flushFnsHandleRef.current) {
        try {
          await flushFnsHandleRef.current();
        } catch (err) {
          log.warn('leave.flush.failed', {
            data: { err: err instanceof Error ? err.message : String(err) },
          });
        }
      }
      pendingLeaveResolver?.(allowed);
      setPendingLeaveResolver(null);
      setOpenModal('none');
      captureCustomEvent(
        allowed ? 'flow_lifecycle.leave_confirmed' : 'flow_lifecycle.leave_cancelled',
        { flowId }
      );
    },
    [pendingLeaveResolver, flowId, log]
  );

  useFlowLeaveGuard({
    enabled: isDirty,
    onAttemptedLeave,
  });

  // ── Journey-switch guard ─────────────────────────────────────────
  // The Wouter leave guard above only catches URL-based navigation
  // (pushState/popstate). The journey switcher in the global header
  // updates `JourneyInstanceContext` directly — without the registry
  // below the host subtree (keyed on journeyInstanceId) remounts for
  // the new journey and the prompt is destroyed mid-render.
  //
  // The journey-switch redirect itself is NOT done here. The per-
  // host `journeyChanged` state lock (CheckUpAssessmentPage et al.)
  // owns navigation — that lock returns null + navigates as soon as
  // it observes activeInstance flipping mid-flight.
  //
  // The guard registers ONLY when there's unflushed work — switches
  // with no dirty work pass through unimpeded and the per-host lock
  // handles the navigation.
  const { registerSwitchGuard } = useJourneyInstance();
  useEffect(() => {
    if (!isDirty) {
      log.debug('switch-guard.idle', { data: { isDirty } });
      return;
    }
    log.debug('switch-guard.registered');
    const unregister = registerSwitchGuard(async (intendedInstanceId) => {
      captureCustomEvent('flow_lifecycle.journey_switch_attempt', {
        flowId,
        fromJourneyId: scopedJourneyId,
        toJourneyId: intendedInstanceId,
      });
      const allowed = await onAttemptedLeave(`journey:${intendedInstanceId ?? 'none'}`);
      log.info('switch-guard.resolved', { data: { allowed } });
      if (allowed) {
        // The user has explicitly chosen to leave their dirty work
        // behind. SYNCHRONOUSLY disarm the URL `useFlowLeaveGuard`
        // BEFORE returning to the journey-switch caller — the caller
        // (`JourneyInstanceContext.handleSetActiveInstance`) calls
        // `setLocation('/dashboard')` immediately after this guard
        // resolves. If we just call `setIsDirty(false)`, React
        // batches the update and the patched `pushState` still has
        // `enabled: true` from the previous render closure → it
        // intercepts the redirect and tries to open ANOTHER leave
        // modal inside an FLM that is unmounting via the keyed
        // remount. The promise from that second intercept never
        // resolves; the original `pushState` is never called; the
        // URL stays on `/checkup/assessment`.
        //
        // `flushSync` forces React to commit the state update + run
        // the `useFlowLeaveGuard` cleanup (which restores the
        // original `pushState`) before we yield control back to the
        // caller. Mirrors `handleDiscardConfirm` /
        // `handleRestartConfirm` above.
        flushSync(() => setIsDirty(false));
      }
      return allowed;
    });
    return () => {
      log.debug('switch-guard.unregistered');
      unregister();
    };
  }, [isDirty, registerSwitchGuard, onAttemptedLeave, flowId, scopedJourneyId, log]);

  // ── Widget state context ──────────────────────────────────────────
  const reportDirty = useCallback((dirty: boolean) => setIsDirty(dirty), []);
  // Hosts call this from `<CoPilotGuide onProgressChange>`. We derive
  // `isDirty` here too so callers don't have to call both.
  const reportProgress = useCallback((percent: number) => {
    setLivePercent(percent);
    setIsDirty(percent > 0 && percent < 100);
  }, []);

  // Set of per-widget flush fns. Each `useWidgetState` registers its
  // flush on mount; the manager's `flushPendingWidgetPatches` calls
  // them all in parallel right before `finalize` so the server has the
  // canonical widget snapshot at the moment status flips to completed.
  //
  // The reactive `registeredWidgetCount` mirrors the set's size for
  // consumers (notably the leave guard) that want to know whether
  // ANY widget could have an in-flight patch without holding a ref
  // back into a private mutable Set.
  const flushFnsRef = useRef<Set<() => Promise<void>>>(new Set());
  const [registeredWidgetCount, setRegisteredWidgetCount] = useState(0);
  const registerFlush = useCallback((flush: () => Promise<void>) => {
    flushFnsRef.current.add(flush);
    setRegisteredWidgetCount(flushFnsRef.current.size);
    return () => {
      flushFnsRef.current.delete(flush);
      setRegisteredWidgetCount(flushFnsRef.current.size);
    };
  }, []);
  const flushPendingWidgetPatches = useCallback(async () => {
    const fns = Array.from(flushFnsRef.current);
    if (fns.length === 0) return;
    await Promise.allSettled(fns.map((fn) => fn()));
  }, []);
  // Wire the function into the ref the leave-guard reads, so
  // `resolveLeave` (declared above this block) can call it without
  // a use-before-decl. Same pattern as the parent `processChatRef`.
  useEffect(() => {
    flushFnsHandleRef.current = flushPendingWidgetPatches;
    return () => {
      flushFnsHandleRef.current = null;
    };
  }, [flushPendingWidgetPatches]);

  const widgetCtx: FlowLifecycleContextValue = useMemo(
    () => ({
      flowId,
      widgetStates: (guideState?.widgetStates as Record<string, Record<string, unknown>>) ?? {},
      patchWidgetState: async (widgetKey, partial) => {
        try {
          await patchWidgetStateMutation.mutateAsync({
            flowId,
            journeyId: scopedJourneyId,
            widgetKey,
            partial,
          });
          // Autosave success → drives <AutosaveIndicator> "Saved · Ns ago".
          setLastSavedAt(new Date());
          setHasSaveError(false);
        } catch {
          // Optimistic local state survives; flag the indicator only.
          // Next successful patch clears it.
          setHasSaveError(true);
        }
      },
      registerFlush,
      flushPendingWidgetPatches,
      registeredWidgetCount,
    }),
    [
      flowId,
      scopedJourneyId,
      guideState?.widgetStates,
      patchWidgetStateMutation,
      registerFlush,
      flushPendingWidgetPatches,
      registeredWidgetCount,
    ]
  );

  // ── Controls passed to host ────────────────────────────────────────
  const controls: FlowLifecycleControls & {
    reportDirty: (dirty: boolean) => void;
    /**
     * Preferred over `reportDirty` — hosts pipe their CoPilotGuide
     * `onProgressChange` here so the modal copy reflects the LIVE
     * progress (server progress is debounced 2s and would otherwise
     * be stale at the moment the user clicks "Leave").
     */
    reportProgress: (percent: number) => void;
  } = {
    startRestart: () => setOpenModal('restart'),
    startDiscard: () => setOpenModal('discard'),
    reportDirty,
    reportProgress,
  };

  // currentStep / totalSteps for the progress bar.
  //
  // Hosts pass `totalSteps` (count of user-visible tasks in the guide
  // config — `tasks.filter(t => t.type !== 'Terminal' && t.type !==
  // 'Escape').length`) so the modal can show an honest "Step N of M".
  //
  // The legacy fallback computed total from `completedSteps / progress`
  // — that produced "Step 1 of 1 — 33%" when `completedSteps === 0`
  // because `Math.round(0 / 33 * 100) = 0`, then `Math.max(1, 0) = 1`.
  // We now prefer the host value; the heuristic stays as a last resort
  // for hosts that haven't been updated yet.
  const completedSteps = guideState?.completedTasks?.length ?? 0;
  const currentStep = Math.min(completedSteps + 1, totalStepsFromHost ?? Number.POSITIVE_INFINITY);
  const totalSteps =
    totalStepsFromHost ??
    (guideState?.progress && guideState.progress > 0 && completedSteps > 0
      ? Math.max(currentStep, Math.round((completedSteps / guideState.progress) * 100))
      : Math.max(currentStep, completedSteps + 1));

  // Effective progress for modal copy: prefer the host's LIVE
  // reportProgress() value over the server-persisted one. Server
  // saves are debounced ~2s; at the moment the user clicks "Leave"
  // the live engine has the truth.
  const effectiveProgress = livePercent ?? guideState?.progress ?? 0;

  // Hide the renderer for `discard` modal when no config is set
  const discardConfigSafe = config.discard ?? config.leave;

  // The lifecycle state the render-prop hands its host.
  const lifecycleState: FlowLifecycleState = {
    isSyncing: patchWidgetStateMutation.isPending,
    isDirty,
    openModal,
    lastSavedAt,
    hasSaveError,
  };

  return (
    <FlowLifecycleContext.Provider value={widgetCtx}>
      {children(controls, lifecycleState)}

      {/* ResumeModal removed 2026-04-27 — see comment block in
          place of the resume-effect above. The auto-modal was the
          single biggest source of state-machine drift bugs; it's
          replaced by direct chat-history rendering + bottom Restart/
          Cancel actions wired through `controls.startRestart` /
          `controls.startDiscard`, plus a host-page Retake modal on
          the results view. */}

      <ConfirmModal
        open={openModal === 'leave'}
        config={config.leave}
        context={{
          progressPercent: effectiveProgress,
          currentStep,
          totalSteps,
        }}
        destructive={false}
        intent="leave"
        onConfirm={() => resolveLeave(true)}
        onCancel={() => resolveLeave(false)}
      />

      <ConfirmModal
        open={openModal === 'restart'}
        config={config.restart}
        context={{
          progressPercent: effectiveProgress,
          currentStep,
          totalSteps,
        }}
        busy={restartMutation.isPending}
        intent="restart"
        onConfirm={handleRestartConfirm}
        onCancel={closeModal}
      />

      <ConfirmModal
        open={openModal === 'discard'}
        config={discardConfigSafe}
        context={{
          progressPercent: effectiveProgress,
          currentStep,
          totalSteps,
        }}
        busy={discardMutation.isPending}
        intent="discard"
        onConfirm={handleDiscardConfirm}
        onCancel={closeModal}
      />

      {/* RetakeModal removed 2026-04-27 — retake moved to host-page
          modals (RetakeConfirmModal). Hosts open it directly on their
          results view (e.g. CheckUpPage, QultureUpPage). */}
    </FlowLifecycleContext.Provider>
  );
}

export { FlowLifecycleContext } from './flow-lifecycle-context';
export { useWidgetState } from './use-widget-state';
export { flowLifecycleRegistry } from './registry';
export type { FlowId, FlowLifecycleControls, FlowLifecycleState } from './types';
